<template>
  <div class="agreement-table">
    <header class="head">
      <div class="head-row">
        <DiviiLogo class="brand-logo" />
        <div class="head-titles">
          <h1>Holiday Schedule</h1>
          <p class="subhead">
            <strong>{{ partyAName || 'Party A' }}</strong> &amp; <strong>{{ partyBName || 'Party B' }}</strong>
          </p>
        </div>
      </div>
    </header>

    <p v-if="!groups.length" class="empty-state">
      No arrangements decided yet. Configure holidays on the left to populate this table.
    </p>

    <template v-else>
      <p v-if="discussionCount > 0" class="discuss-banner">
        <strong>{{ discussionCount }} {{ discussionCount === 1 ? 'item is' : 'items are' }} still under discussion.</strong>
        Marked rows in the table below are not yet finalized.
      </p>

      <table class="single-table">
        <thead>
          <tr>
            <th class="col-name">Holiday</th>
            <th class="col-arr">Arrangement</th>
            <th class="col-who">In the care of</th>
          </tr>
        </thead>
        <tbody v-for="cat in groups" :key="cat.key">
          <tr class="cat-row">
            <td colspan="3">{{ cat.label }}</td>
          </tr>
          <tr v-for="h in cat.list" :key="h.id" :class="{ pending: !isConfigured(h), flagged: h.flaggedForDiscussion }">
            <td class="td-name">
              {{ h.name }}<span v-if="h.flaggedForDiscussion" class="row-discuss-tag"> (under discussion)</span>
            </td>
            <td>
              <div class="arr-cell">
                <span class="arr-label">{{ arrangementLabel(h) }}</span>
                <span v-if="isLongWeekendScope(h)" class="lw-tag">LW</span>
              </div>
            </td>
            <td class="td-who">
              <!-- On screen: when the user has overridden the prose with
                   custom wording, render that directly (matches the PDF).
                   Otherwise: short descriptive summary + optional timing
                   + optional notes, each on its own line.
                   In the PDF: always the full Agreement-style prose. -->
              <template v-if="h.customProse && h.customProse.trim()">
                <span class="care-who-short care-custom">{{ h.customProse.trim() }}</span>
              </template>
              <template v-else>
                <span :class="['care-who-short', whoChipClass(h)]">{{ whoLabel(h) }}</span>
                <span v-if="timesLabel(h)" class="care-time">{{ timesLabel(h) }}</span>
                <span v-if="noteText(h)" class="care-note">{{ noteText(h) }}</span>
              </template>
              <span class="care-who-full">{{ whoLongLabel(h) }}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </template>
  </div>
</template>

<script>
import { CATEGORIES } from '../seed/holidays.js'
import { formatTimeOfDay, renderClause } from '../proseRenderer.js'
import DiviiLogo from './DiviiLogo.vue'

const CATEGORY_ORDER = ['holiday', 'special-day', 'school-break', 'christmas']
const UNDECIDED_KINDS = new Set(['unset', 'agreement-required'])
const PROVINCE_NAMES = {
  AB: 'Alberta', BC: 'British Columbia', MB: 'Manitoba',
  NB: 'New Brunswick', NL: 'Newfoundland & Labrador', NS: 'Nova Scotia',
  NT: 'Northwest Territories', NU: 'Nunavut', ON: 'Ontario',
  PE: 'Prince Edward Island', SK: 'Saskatchewan', YT: 'Yukon',
}

function nameFor(parent, partyAName, partyBName) {
  if (parent === 'A') return partyAName || 'Party A'
  if (parent === 'B') return partyBName || 'Party B'
  return ''
}

export default {
  name: 'AgreementTable',
  components: { DiviiLogo },
  props: {
    holidays: { type: Array, required: true },
    partyAName: { type: String, default: '' },
    partyBName: { type: String, default: '' },
    jurisdiction: { type: String, default: '' },
    children: { type: Array, default: () => [] },
  },
  computed: {
    today() {
      return new Date().toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' })
    },
    jurisdictionLabel() {
      return PROVINCE_NAMES[this.jurisdiction] || this.jurisdiction
    },
    kidsLabel() {
      if (this.children.length === 1) return this.children[0]?.name || 'the child'
      if (this.children.length >= 2) return 'the Children'
      return 'the children'
    },
    groups() {
      const byCategory = {}
      for (const h of this.holidays) {
        if (UNDECIDED_KINDS.has(h?.rule?.kind)) continue
        ;(byCategory[h.category] ||= []).push(h)
      }
      return CATEGORY_ORDER
        .filter((k) => byCategory[k]?.length)
        .map((k) => ({ key: k, label: CATEGORIES[k], list: byCategory[k] }))
    },
    // Count of decided rows that are also flagged for discussion. Drives the
    // banner above the table — undecided flagged rows aren't shown here.
    discussionCount() {
      let n = 0
      for (const cat of this.groups) {
        for (const h of cat.list) if (h.flaggedForDiscussion) n++
      }
      return n
    },
  },
  methods: {
    isConfigured(h) {
      return h.rule.kind !== 'unset' && h.rule.kind !== 'agreement-required'
    },
    arrangementLabel(h) {
      const kind = h.rule.kind
      const map = {
        'unset':               'Needs decision',
        'agreement-required':  'Needs decision',
        'no-change':           'No change',
        'suspend-flex':        'Flexible approach',
        'fixed':               'One parent',
        'alternating-by-year': 'Alternate yearly',
        'split-day':           'Split the day',
        'split-period':        'Split the holiday',
        'share':               h.category === 'school-break' ? 'Share as agreed' : 'Share together',
        'week-on-off':         'Week on/off',
        'paired-day-split':    'Eve/Day split, alt.',
        'tie-to-weekend':      'Tied to weekend',
      }
      return map[kind] || ''
    },
    isLongWeekendScope(h) {
      return h.rule.scope === 'long-weekend' || h.rule.kind === 'tie-to-weekend'
    },
    // The free-form note for a row. customProse renders separately as the
    // main "who" line now, so this only carries the user's plain notes.
    noteText(h) {
      if (h.notes && h.notes.trim()) return h.notes.trim()
      return ''
    },
    // Full legal-prose version of the "in the care of" cell. Uses the same
    // renderClause as the Agreement view, stripped of the "For [holiday], "
    // prefix (the holiday name is already in the leftmost column). Notes
    // and pickup/dropoff are already woven into the prose, so the print
    // layout doesn't duplicate them as separate lines.
    whoLongLabel(h) {
      const parties = {
        partyAName: this.partyAName,
        partyBName: this.partyBName,
        children: this.children,
      }
      const prose = renderClause(h, parties)
      const prefix = `For ${h.name}, `
      if (prose.startsWith(prefix)) {
        const body = prose.slice(prefix.length)
        return body.charAt(0).toUpperCase() + body.slice(1)
      }
      return prose
    },
    whoLabel(h) {
      const A = this.partyAName || 'Party A'
      const B = this.partyBName || 'Party B'
      const r = h.rule
      switch (r.kind) {
        case 'fixed': {
          const who = nameFor(r.parent, this.partyAName, this.partyBName)
          return `${who} each year`
        }
        case 'alternating-by-year': {
          const evenName = nameFor(r.evenParent, this.partyAName, this.partyBName)
          const oddName = nameFor(r.evenParent === 'A' ? 'B' : 'A', this.partyAName, this.partyBName)
          return `${evenName} in even years; ${oddName} in odd years`
        }
        case 'split-day': {
          const firstName = nameFor(r.firstHalfParent, this.partyAName, this.partyBName)
          const secondName = nameFor(r.firstHalfParent === 'A' ? 'B' : 'A', this.partyAName, this.partyBName)
          if (r.transitionByAgreement) {
            return `${firstName} for the first part of the day, then ${secondName} — transition time agreed by the parties`
          }
          const t = formatTimeOfDay(r.transitionTime || '12:00')
          return `${firstName} until ${t}, then ${secondName}`
        }
        case 'split-period': {
          const firstName = nameFor(r.firstHalfParent, this.partyAName, this.partyBName)
          const secondName = nameFor(r.firstHalfParent === 'A' ? 'B' : 'A', this.partyAName, this.partyBName)
          return `${firstName} for the first half, ${secondName} for the second`
        }
        case 'week-on-off': {
          const startName = nameFor(r.startParent, this.partyAName, this.partyBName)
          const otherName = nameFor(r.startParent === 'A' ? 'B' : 'A', this.partyAName, this.partyBName)
          return `${startName} the first week, alternating with ${otherName} thereafter`
        }
        case 'paired-day-split': {
          const evenName = nameFor(r.evenParent, this.partyAName, this.partyBName)
          const oddName = nameFor(r.evenParent === 'A' ? 'B' : 'A', this.partyAName, this.partyBName)
          return `${evenName} in even years; ${oddName} in odd years`
        }
        case 'share':            return `${A} and ${B} together`
        case 'tie-to-weekend':   return 'Whichever parent has the adjacent weekend'
        case 'no-change':        return 'Per the regular parenting schedule'
        case 'suspend-flex':     return 'Decide together, with reasonable notice'
        default:                 return ''
      }
    },
    timesLabel(h) {
      // "Times to be agreed by the parties" overrides pickup/dropoff display.
      // Paired-day-split's structural splitTime is unaffected.
      if (h.timesByAgreement && h.rule.kind !== 'paired-day-split') {
        return 'At times to be agreed by the parties'
      }
      if (h.rule.kind === 'paired-day-split') {
        const t = formatTimeOfDay(h.rule.splitTime || '12:00')
        return `${t} → ${t}`
      }
      const p = formatTimeOfDay(h.pickup)
      const d = formatTimeOfDay(h.dropoff)
      if (p && d) return `${p} → ${d}`
      if (p)      return `From ${p}`
      if (d)      return `Until ${d}`
      return ''
    },
    whoChipClass(h) {
      const k = h.rule.kind
      if (k === 'fixed' && h.rule.parent === 'A') return 'who-chip chip-a'
      if (k === 'fixed' && h.rule.parent === 'B') return 'who-chip chip-b'
      if (k === 'alternating-by-year' || k === 'split-day' || k === 'split-period' || k === 'week-on-off' || k === 'paired-day-split') return 'who-chip chip-mixed'
      if (k === 'share') return 'who-chip chip-share'
      if (k === 'tie-to-weekend') return 'who-chip chip-share'
      return 'who-text'
    },
  },
}
</script>

<style scoped>
.agreement-table {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 1.25rem 1.4rem;
  font-size: 0.85rem;
  color: #1e293b;
}

.head { margin-bottom: 0.85rem; }
.empty-state {
  margin: 1rem 0;
  padding: 1rem;
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
  text-align: center;
  color: #64748b;
  font-size: 0.85rem;
  font-style: italic;
}
.head-row {
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;
  border-left: 3px solid #f7a91b;
  padding-left: 0.7rem;
}
.brand-logo { flex-shrink: 0; margin-top: 0.1rem; }
.head-titles { flex-grow: 1; min-width: 0; }
.head h1 {
  margin: 0 0 0.15rem;
  font-size: 1.35rem;
  font-weight: 700;
  color: #34555f;
  letter-spacing: -0.01em;
}
.subhead {
  margin: 0 0 0.85rem;
  font-size: 0.85rem;
  color: #475569;
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  align-items: baseline;
}
.subhead strong { color: #1e293b; font-weight: 600; }

.single-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.82rem;
}
.single-table th, .single-table td {
  text-align: left;
  padding: 0.45rem 0.55rem;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: top;
  /* Default to wrap on screen so long content (e.g. "Even: Chris · Odd: Gwyneth")
     can break across lines. Print rules below force nowrap so the dense table
     stays on a single page at letter width. */
  white-space: normal;
}
.single-table th {
  background: #244047;
  color: #fff;
  font-weight: 700;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding: 0.5rem 0.55rem;
  vertical-align: middle;
}

/* On screen the panel is narrow (~470px). Hide the Times and Notes columns
   so the remaining 3 (Holiday · Arrangement · In the care of) get enough
   room to render without ellipsis-clipping. The dropped data still appears
   in the printed PDF (where the table widens to letter-page width). */
@media not print {
  .single-table .print-only { display: none; }
}

tr.cat-row td {
  background: #eef3f4;
  color: #34555f;
  font-weight: 700;
  font-size: 0.65rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 0.32rem 0.45rem;
  border-bottom: 1px solid #cbd5e1;
}

.td-name { font-weight: 500; }
.td-time { font-variant-numeric: tabular-nums; color: #475569; white-space: nowrap; }
.notes-cell { color: #64748b; font-style: italic; font-size: 0.7rem; }
tr.pending td { color: #94a3b8; font-style: italic; }
tr.pending .td-name { color: #1e293b; font-style: normal; font-weight: 500; }

/* Banner shown above the table when at least one decided row is flagged. */
.discuss-banner {
  margin: 0 0 0.7rem;
  font-size: 0.78rem;
  color: #85581a;
  border-left: 3px solid #f7a91b;
  padding: 0.45rem 0.75rem;
  background: #fef6e0;
  border-radius: 0 6px 6px 0;
}
.discuss-banner strong { color: #85581a; }

/* Flagged rows: amber tint + inline tag next to the holiday name. */
tr.flagged td { background: #fef6e0; }
.row-discuss-tag {
  font-style: italic;
  color: #85581a;
  font-weight: 500;
  font-size: 0.92em;
  margin-left: 0.3rem;
  white-space: nowrap;
}

/* Arrangement cell layout: label on the left, LW pill (when present)
   right-aligned so the pills line up consistently across all rows. */
.arr-cell {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.4rem;
}
.arr-label { flex-grow: 1; }
.lw-tag {
  display: inline-block;
  flex-shrink: 0;
  padding: 0.05rem 0.4rem;
  background: #fde9bf;
  color: #85581a;
  border-radius: 4px;
  font-size: 0.6rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  vertical-align: middle;
}
/* "In the care of" cell — two parallel renders inside the same td:
   - Screen: short descriptive summary + optional times + optional notes
   - PDF (@media print): full Agreement-style prose, swapped via CSS */
.care-who-short {
  display: block;
  font-weight: 500;
  font-size: 0.82rem;
  line-height: 1.4;
}
.care-who-full {
  display: none;
}
.care-time {
  display: block;
  font-size: 0.78rem;
  color: #475569;
  font-variant-numeric: tabular-nums;
  margin-top: 0.15rem;
}
.care-note {
  display: block;
  font-size: 0.78rem;
  color: #64748b;
  font-style: italic;
  margin-top: 0.1rem;
}
.chip-a       { color: #244047; font-weight: 600; }
.chip-b       { color: #85581a; font-weight: 600; }
.chip-mixed   { color: #34555f; }
.chip-share   { color: #34555f; }
.who-text     { color: #64748b; font-style: italic; font-weight: 400; }

/* Three columns; "In the care of" gets the most room since it now holds
   the descriptive sentence + optional timing + optional notes stacked. */
.col-name  { width: 22%; }
.col-arr   { width: 22%; }
.col-who   { width: 56%; }

/* Print: aggressive compression so 24 holidays + descriptive "In the care of"
   text + header + section bands fit on one US-letter page. */
@media print {
  .agreement-table {
    border: none;
    border-radius: 0;
    padding: 0;
    box-shadow: none;
    overflow: visible;
    /* Don't lock the whole table to one page — with full Agreement prose
       per row, content can exceed a single letter page. Locking it caused
       browsers to push the entire table to page 2, leaving page 1 blank. */
  }
  .head { margin-bottom: 0.35rem; }
  .head h1 { font-size: 1.1rem; }
  .subhead { font-size: 0.62rem; }
  .single-table { font-size: 0.62rem; line-height: 1.3; }
  /* Keep section bands glued to the first row of their section so the
     uppercase HOLIDAYS / SPECIAL DAYS / etc header doesn't strand alone
     at the bottom of a page. */
  tr.cat-row {
    page-break-after: avoid;
    break-after: avoid;
  }
  /* Three columns. "In the care of" gets the lion's share since it carries
     the descriptive sentence + timing + notes. */
  .col-name  { width: 22%; }
  .col-arr   { width: 22%; }
  .col-who   { width: 56%; }
  .single-table th, .single-table td {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    padding: 0.16rem 0.34rem;
    vertical-align: top;
  }
  .single-table td.td-who { white-space: normal; }
  /* Force the dark teal background + white text to actually render in
     the printed PDF (browsers strip color backgrounds in print by default
     unless the user toggles "Background graphics"). */
  .single-table th {
    padding: 0.3rem 0.4rem;
    font-size: 0.66rem;
    background: #244047 !important;
    color: #fff !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  tr.cat-row td {
    padding: 0.22rem 0.34rem;
    font-size: 0.62rem;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  /* Swap: PDF uses the full Agreement prose; the short summary + the
     separate time/note lines hide (the prose already includes them). */
  .care-who-short,
  .care-time,
  .care-note { display: none; }
  .care-who-full {
    display: block;
    font-size: 0.62rem;
    line-height: 1.35;
    color: #1e293b;
    font-weight: 400;
  }
  tr { page-break-inside: avoid; }
}
</style>
